import styles from "./Portfolio.module.scss";
import React, {useContext} from "react";
import useFormat from "../../../util/useFormat";
import ColoredAmount from "../../../common/ColoredAmount";
import clsx from "clsx";
import {HeaderMenu} from "../../../common/HeaderMenu";
import {PortfolioInfos} from "./PortfolioInfos";
import {Link} from "react-router";
import {useSelector} from "react-redux";
import {appConfigSelector} from "../../../slices/appSlice";
import {useTranslation} from "react-i18next";
import {Content, ContentFixed, ContentScroll} from "../../../common/Content";
import {AppContext} from "../../../App";

const Portfolio = () => {
  const {quotes} = useContext(AppContext).portfolio;

  const {formatAmount} = useFormat();

  const {portfolioCurrency} = useSelector(appConfigSelector);
  const {t} = useTranslation("portfolio");

  const comparator = (a, b) => {
    if (a.marketValue === b.marketValue) {
      return a.symbol.localeCompare(b.symbol);
    } else {
      return b.marketValue - a.marketValue;
    }
  };

  return <Content>
    <ContentFixed>
      <HeaderMenu title={t("header_title_portfolio")}/>

      <PortfolioInfos quotes={quotes}/>

      <div className={clsx(styles.quote, styles.header)}>
        <span className={styles.symbol}>{t("list_header_symbol")}</span>
        <span className={styles.price}>{t("list_header_price")}</span>
        <span className={styles.priceVariation}>{t("list_header_price_variation")}</span>
        <span className={styles.quantity}>{t("list_header_quantity")}</span>
        <span className={styles.cost}>{t("list_header_cost")}</span>
        <span className={styles.value}>{t("list_header_value")}</span>
        <span className={styles.pru}>{t("list_header_pru")}</span>
        <span className={styles.gains}>{t("list_header_daily_gains")}</span>
        <span className={styles.gains}>{t("list_header_total_gains")}</span>
      </div>
    </ContentFixed>

    <ContentScroll whiteBackground className={clsx(styles.contentScroll, "autosizer-scrollbar")}>
      {Object.values(quotes).filter(q => q.favorite || q.quantity > 0).toSorted(comparator).map(quote => {
        let priceDiff = quote.price - quote.previousClose;
        let priceClass = null;

        if (priceDiff > 0) {
          priceClass = styles.positive;
        } else if (priceDiff < 0) {
          priceClass = styles.negative;
        }

        const dayGainsSign = quote.dayGains > 0 ? "+" : "";
        const dayGainsPct = ((quote.marketValue / quote.dayOpenValue * 100) - 100).toFixed(2);

        const ongoingGainsSign = quote.ongoingGains > 0 ? "+" : "";
        const ongoingGainsPct = ((quote.marketValue / quote.initialValue * 100) - 100).toFixed(2);

        return <Link to={"/portfolio/" + quote.symbol} className={clsx(styles.quote, "hover-selectable")}
                     key={quote.symbol}>
          <span className={styles.symbol}>
            <span>
              {quote.symbol}
              {quote.quantity > 0 && <span className={styles.quantityMobile}> ({quote.quantity})</span>}
            </span>

            <span className={styles.name}>
              <span className={styles.priceMobile}>{formatAmount(quote.price, quote.currency)} | </span>
              {quote.name}
            </span>
          </span>
          <span className={styles.price}>{formatAmount(quote.price, quote.currency)}</span>
          <span className={clsx(styles.priceVariation, priceClass)}>
            <span>{(priceDiff > 0 ? "+" : "") + ((quote.price / quote.previousClose * 100) - 100).toFixed(2) + " %"}</span>
          </span>
          <span className={styles.quantity}>{quote.quantity > 0 ? quote.quantity : "-"}</span>
          <span className={styles.cost}>
            {quote.quantity > 0 ? formatAmount(quote.initialValue, portfolioCurrency) : "-"}
          </span>
          <span className={styles.value}>
            {quote.quantity > 0 ? formatAmount(quote.marketValue, portfolioCurrency) : "-"}
          </span>
          <span className={styles.pru}>{quote.quantity > 0 ? formatAmount(quote.pru, quote.currency) : "-"}</span>
          <span className={styles.gains}>
            {quote.quantity > 0 ? <ColoredAmount amount={quote.dayGains}>
              {dayGainsSign + formatAmount(quote.dayGains, portfolioCurrency)}
              {" "}
              ({(dayGainsSign + dayGainsPct)}%)
            </ColoredAmount> : "-"}
          </span>
          <span className={styles.gains}>
            {quote.transactions.length > 0 ? <ColoredAmount amount={quote.ongoingGains}>
              {ongoingGainsSign + formatAmount(quote.ongoingGains, portfolioCurrency)}
              {quote.quantity > 0 ? " (" + (ongoingGainsSign + ongoingGainsPct) + "%)": ""}
            </ColoredAmount> : "-"}
          </span>
          <span className={styles.customValuesMobile}>
            {quote.quantity > 0 && <span className={styles.value1}>
              {formatAmount(quote.marketValue, portfolioCurrency)}
            </span>}

            <span className={styles.value2}>
            {quote.transactions.length > 0 ? <ColoredAmount amount={quote.ongoingGains}>
              {ongoingGainsSign + formatAmount(quote.ongoingGains, portfolioCurrency)}
            </ColoredAmount> : ""}
            </span>
          </span>
        </Link>;
      })}
    </ContentScroll>
  </Content>;
};

export default Portfolio;
